import apiClient from "@/api/client";
import type { IssueAttachment, IssueComment, TechnicalIssue } from "@/features/dev_issues/types";

const wrap = <T>(res: { data: { data: T } }) => res.data.data;

export type IssueListMeta = {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
};

export type IssueReporter = { id: string; name: string };

export const devIssuesApi = {
  list: (params?: { status?: string; priority?: string; category?: string; title?: string; reporter?: string; assignee?: string; my_issues?: boolean; page?: number; ordering?: string; ordering_dir?: string }) =>
    apiClient
      .get<{ data: TechnicalIssue[]; meta: IssueListMeta }>("/dev-issues/", { params })
      .then((res) => ({ issues: res.data.data, meta: res.data.meta })),

  listReporters: () =>
    apiClient.get<{ data: IssueReporter[] }>("/dev-issues/reporters/").then(wrap),

  listAssignees: () =>
    apiClient.get<{ data: IssueReporter[] }>("/dev-issues/assignees/").then(wrap),

  get: (id: string) =>
    apiClient.get<{ data: TechnicalIssue }>(`/dev-issues/${id}/`).then(wrap),

  create: (data: { title: string; description: string }) =>
    apiClient.post<{ data: TechnicalIssue }>("/dev-issues/", data).then(wrap),

  update: (id: string, data: Partial<Pick<TechnicalIssue, "title" | "description" | "category" | "priority" | "status">>) =>
    apiClient.patch<{ data: TechnicalIssue }>(`/dev-issues/${id}/`, data).then(wrap),

  resolve: (id: string) =>
    apiClient.post<{ data: TechnicalIssue }>(`/dev-issues/${id}/resolve/`, {}).then(wrap),

  wontFix: (id: string) =>
    apiClient.post<{ data: TechnicalIssue }>(`/dev-issues/${id}/wont-fix/`, {}).then(wrap),

  reopen: (id: string) =>
    apiClient.post<{ data: TechnicalIssue }>(`/dev-issues/${id}/reopen/`, {}).then(wrap),

  take: (id: string) =>
    apiClient.post<{ data: TechnicalIssue }>(`/dev-issues/${id}/take/`, {}).then(wrap),

  unassign: (id: string) =>
    apiClient.post<{ data: TechnicalIssue }>(`/dev-issues/${id}/unassign/`, {}).then(wrap),

  addParticipant: (id: string, userId: string) =>
    apiClient
      .post<{ data: TechnicalIssue }>(`/dev-issues/${id}/add-participant/`, { user_id: userId })
      .then(wrap),

  removeParticipant: (id: string, userId: string) =>
    apiClient
      .post<{ data: TechnicalIssue }>(`/dev-issues/${id}/remove-participant/`, { user_id: userId })
      .then(wrap),

  listComments: (issueId: string) =>
    apiClient.get<{ data: IssueComment[] }>(`/dev-issues/${issueId}/comments/`).then(wrap),

  addComment: (issueId: string, content: string, mentionIds: string[] = []) =>
    apiClient.post<{ data: IssueComment }>(`/dev-issues/${issueId}/comments/`, {
      content,
      mention_ids: mentionIds,
    }).then(wrap),

  listAttachments: (issueId: string) =>
    apiClient.get<{ data: IssueAttachment[] }>(`/dev-issues/${issueId}/attachments/`).then(wrap),

  uploadAttachment: (issueId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiClient.post<{ data: IssueAttachment }>(`/dev-issues/${issueId}/attachments/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(wrap);
  },

  delete: (id: string) =>
    apiClient.delete(`/dev-issues/${id}/`),

  deleteAttachment: (issueId: string, attachmentId: string) =>
    apiClient.delete(`/dev-issues/${issueId}/attachments/${attachmentId}/`),

  uploadCommentAttachment: (issueId: string, commentId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiClient.post<{ data: IssueAttachment }>(
      `/dev-issues/${issueId}/comments/${commentId}/attachments/`, form,
      { headers: { "Content-Type": "multipart/form-data" } }
    ).then(wrap);
  },

  // Fresh presigned link for an attachment's ManagedFile. `attachment.url` is
  // signed when the issue is fetched and dies an hour later ("Request has
  // expired"), while the issue page happily stays open longer than that.
  freshFileUrl: (managedFileId: string) =>
    apiClient
      .get<{ data: { url: string | null; original_filename: string } }>(`/files/${managedFileId}/`)
      .then(wrap),

  uploadDescriptionImage: (file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    return apiClient
      .post<{ data: { url: string } }>(
        `/dev-issues/upload-description-image/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((res) => res.data.data.url);
  },

  uploadCommentImage: (issueId: string, file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file);
    return apiClient
      .post<{ data: { url: string } }>(
        `/dev-issues/${issueId}/comments/upload-image/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((res) => res.data.data.url);
  },
};
